import * as React from "react";
import { useState, useEffect, useRef } from "react";

import axios from "axios";

import {
  Button,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(2, 3),
    overflow: "hidden",
  },
  topBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
    width: "100%",
    "& > *": {
      marginRight: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
    },
  },
  tableContainer: {
    width: "100%",
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(3),
    flexWrap: "wrap",
    "& > *": {
      marginRight: theme.spacing(1.5),
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.grey[500],
  },
  blockedChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  flagChip: {
    margin: 0,
  },
  // ── detail ──────────────────────────────────────────────────
  detailCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2.5),
    width: "100%",
    boxSizing: "border-box",
  },
  profileHeader: {
    marginBottom: theme.spacing(2.5),
  },
  // Three equal columns; each row of fields sits in a row div
  detailRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    columnGap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    width: "100%",
    boxSizing: "border-box",
  },
  detailField: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  detailLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 2,
  },
  detailValue: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  actionDivider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      marginRight: theme.spacing(1),
    },
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1),
  },
  // ── list ────────────────────────────────────────────────────
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  pageSizeControl: {
    minWidth: 80,
  },
  filterControl: {
    minWidth: 140,
  },
  emailInput: {
    minWidth: 240,
  },
}));

// ─── helpers ────────────────────────────────────────────────────────────────

const formatDate = (d: string | null | undefined) => {
  if (!d) return "N/A";
  return new Date(d).toLocaleString();
};

const FlagChip: React.FC<{ flag: string | null | undefined; watchRemaining?: number | null; className?: string }> = ({ flag, watchRemaining, className }) => {
  if (!flag || flag === "none") return <span>—</span>;
  if (flag === "watching") {
    const label = watchRemaining != null ? `watching (${watchRemaining} left)` : "watching";
    return <Chip label={label} size="small" style={{ backgroundColor: "#ff9800", color: "#fff" }} className={className} />;
  }
  if (flag === "bank_confirmed_fraud") {
    return <Chip label="bank confirmed fraud" size="small" style={{ backgroundColor: "#8b0000", color: "#fff" }} className={className} />;
  }
  const color = flag === "confirmed_fraud" ? "secondary" : "default";
  return <Chip label={flag.replace(/_/g, " ")} size="small" color={color} className={className} />;
};

// ─── component ──────────────────────────────────────────────────────────────

const FraudReview = () => {
  const classes = useStyles();
  const notify = useNotify();

  // view
  const [view, setView] = useState<"list" | "detail">("list");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // list state
  const [listData, setListData] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total: 0,
    total_pages: 0,
    has_next_page: false,
    has_previous_page: false,
  });
  const [filters, setFilters] = useState({
    status: "blocked",
    flag: "",
    email: "",
    page: 1,
    pageSize: 25,
  });
  const [listLoading, setListLoading] = useState(false);

  // detail state
  const [detailData, setDetailData] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // blocked instruments (lazy — loaded when section is expanded)
  const [instrumentsExpanded, setInstrumentsExpanded] = useState(false);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);
  const [instruments, setInstruments] = useState<any[] | null>(null); // null = not yet fetched

  // didit identity verification (lazy, keyed off the profile email)
  const [diditExpanded, setDiditExpanded] = useState(false);
  const [diditLoading, setDiditLoading] = useState(false);
  const [didit, setDidit] = useState<any | null>(null); // null = not yet fetched
  const [instrumentToUnblock, setInstrumentToUnblock] = useState<any | null>(null);
  const [instrumentUnblockReason, setInstrumentUnblockReason] = useState("");

  // dialogs
  const [dialogType, setDialogType] = useState<"block" | "unblock" | "flag" | "clear" | "blockNew" | "instrumentUnblock" | null>(null);
  const [dialogReason, setDialogReason] = useState("");
  const [dialogFlag, setDialogFlag] = useState<"suspicious" | "confirmed_fraud" | "bank_confirmed_fraud" | null>(null);
  const [dialogUserId, setDialogUserId] = useState("");
  const [actionProcessing, setActionProcessing] = useState(false);

  // debounce ref for email
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── list fetch ──────────────────────────────────────────────────────────────

  const fetchList = (f: typeof filters) => {
    setListLoading(true);

    const params: any = { page: f.page, pageSize: f.pageSize };
    if (f.email && f.email.trim()) {
      params.email = f.email.trim();
    } else {
      params.status = f.status || "all";
      if (f.flag && f.flag !== "") params.flag = f.flag;
    }

    axios
      .get(ENDPOINTS.FRAUD_REVIEW, getConfigGet(params))
      .then((res: any) => {
        // Support both { data: { users, pagination } } and { users, pagination }
        const body = res.data?.data ?? res.data;
        const users = body?.users ?? (Array.isArray(body) ? body : []);
        const pag = body?.pagination ?? res.data?.pagination ?? null;
        setListData(users);
        if (pag) setPagination(pag);
        setListLoading(false);
      })
      .catch((err: any) => {
        notify(err?.response?.data?.message || "Failed to fetch fraud review list", "error", "", undefined, 8000);
        setListLoading(false);
      });
  };

  useEffect(() => {
    fetchList(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── detail fetch ────────────────────────────────────────────────────────────

  const fetchDetail = (userId: number) => {
    setDetailLoading(true);
    axios
      .get(`${ENDPOINTS.FRAUD_REVIEW}/${userId}`, getConfigGet({}))
      .then((res: any) => {
        const d = res.data?.data || res.data;
        setDetailData(d);
        setDetailLoading(false);
      })
      .catch((err: any) => {
        notify(err?.response?.data?.message || "Failed to fetch user detail", "error", "", undefined, 8000);
        setDetailLoading(false);
      });
  };

  // ── blocked instruments (lazy) ──────────────────────────────────────────────

  const fetchBlockedInstruments = (userId: number) => {
    setInstrumentsLoading(true);
    axios
      .get(ENDPOINTS.FRAUD_BLOCKED_INSTRUMENT, getConfigGet({ user_id: userId }))
      .then((res: any) => {
        const rows = res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
        setInstruments(rows);
        setInstrumentsLoading(false);
      })
      .catch((err: any) => {
        notify(err?.response?.data?.message || "Failed to fetch blocked instruments", "error", "", undefined, 8000);
        setInstruments([]);
        setInstrumentsLoading(false);
      });
  };

  const toggleInstrumentsSection = () => {
    const next = !instrumentsExpanded;
    setInstrumentsExpanded(next);
    // Lazy-load on first expand
    if (next && instruments === null && selectedUserId) {
      fetchBlockedInstruments(selectedUserId);
    }
  };

  // ── didit identity verification (lazy) ──────────────────────────────────────

  // Unscoped: shows every verification the user has run, so a mortgage-KYC pass is
  // visible here too when diagnosing why the fraud unblock did or didn't happen.
  const fetchDidit = (email: string) => {
    setDiditLoading(true);
    axios
      .get(ENDPOINTS.DIDIT, getConfigGet({ email }))
      .then((res: any) => {
        setDidit(res.data?.data ?? {});
        setDiditLoading(false);
      })
      .catch((err: any) => {
        notify(err?.response?.data?.message || "Failed to fetch identity verification", "error", "", undefined, 8000);
        setDidit({});
        setDiditLoading(false);
      });
  };

  const toggleDiditSection = () => {
    const next = !diditExpanded;
    setDiditExpanded(next);
    if (next && didit === null && detailData?.profile?.email) {
      fetchDidit(detailData.profile.email);
    }
  };

  // ── navigation ──────────────────────────────────────────────────────────────

  const openDetail = (userId: number) => {
    setSelectedUserId(userId);
    setView("detail");
    fetchDetail(userId);
    // Reset lazy sections — user may open a different profile later.
    setInstrumentsExpanded(false);
    setInstruments(null);
    setDiditExpanded(false);
    setDidit(null);
  };

  const backToList = () => {
    setView("list");
    setSelectedUserId(null);
    setDetailData(null);
    setInstrumentsExpanded(false);
    setInstruments(null);
    setDiditExpanded(false);
    setDidit(null);
  };

  // ── filter handlers ─────────────────────────────────────────────────────────

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const updated = { ...filters, email: val, page: 1 };
    setFilters(updated);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchList(updated), 500);
  };

  const handleStatusChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const updated = { ...filters, status: e.target.value as string, page: 1 };
    setFilters(updated);
    fetchList(updated);
  };

  const handleFlagChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const updated = { ...filters, flag: e.target.value as string, page: 1 };
    setFilters(updated);
    fetchList(updated);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const updated = { ...filters, pageSize: e.target.value as number, page: 1 };
    setFilters(updated);
    fetchList(updated);
  };

  const handlePrevPage = () => {
    if (!pagination.has_previous_page) return;
    const updated = { ...filters, page: filters.page - 1 };
    setFilters(updated);
    fetchList(updated);
  };

  const handleNextPage = () => {
    if (!pagination.has_next_page) return;
    const updated = { ...filters, page: filters.page + 1 };
    setFilters(updated);
    fetchList(updated);
  };

  // ── dialog helpers ──────────────────────────────────────────────────────────

  const openDialog = (
    type: "block" | "unblock" | "flag" | "clear" | "blockNew",
    flag?: "suspicious" | "confirmed_fraud" | "bank_confirmed_fraud"
  ) => {
    setDialogType(type);
    setDialogReason("");
    setDialogFlag(flag || null);
    setDialogUserId("");
  };

  const openInstrumentUnblockDialog = (inst: any) => {
    setInstrumentToUnblock(inst);
    setInstrumentUnblockReason("");
    setDialogType("instrumentUnblock");
  };

  const closeDialog = () => {
    setDialogType(null);
    setDialogReason("");
    setDialogFlag(null);
    setDialogUserId("");
    setInstrumentToUnblock(null);
    setInstrumentUnblockReason("");
  };

  // ── action submit ────────────────────────────────────────────────────────────

  const submitAction = (userId: number | string, body: object) => {
    setActionProcessing(true);
    const headers = getConfigPatch();
    axios
      .patch(`${ENDPOINTS.FRAUD_REVIEW}/${userId}`, body, { headers })
      .then(() => {
        notify("Action completed successfully", "info", "", undefined, 5000);
        closeDialog();
        setActionProcessing(false);
        if (view === "detail" && selectedUserId) {
          fetchDetail(selectedUserId);
        } else {
          fetchList(filters);
        }
      })
      .catch((err: any) => {
        notify(err?.response?.data?.message || "Action failed", "error", "", undefined, 8000);
        setActionProcessing(false);
      });
  };

  const handleDialogConfirm = () => {
    if (dialogType === "blockNew") {
      if (!dialogUserId.trim()) return;
      submitAction(dialogUserId.trim(), { action: "block", reason: dialogReason || undefined });
      return;
    }
    if (dialogType === "instrumentUnblock") {
      submitInstrumentUnblock();
      return;
    }
    if (!selectedUserId) return;
    if (dialogType === "block") {
      submitAction(selectedUserId, { action: "block", reason: dialogReason || undefined });
    } else if (dialogType === "unblock") {
      submitAction(selectedUserId, { action: "unblock" });
    } else if (dialogType === "flag") {
      submitAction(selectedUserId, { action: "flag", flag: dialogFlag, reason: dialogReason || undefined });
    } else if (dialogType === "clear") {
      submitAction(selectedUserId, { action: "clear" });
    }
  };

  const submitInstrumentUnblock = () => {
    if (!instrumentToUnblock?.id) return;
    const reason = instrumentUnblockReason.trim();
    if (reason.length < 10) {
      notify("Please provide an unblock reason (min 10 characters)", "warning", "", undefined, 5000);
      return;
    }

    setActionProcessing(true);
    const headers = getConfigPatch();
    axios
      .patch(
        `${ENDPOINTS.FRAUD_BLOCKED_INSTRUMENT}/${instrumentToUnblock.id}`,
        { unblock_reason: reason },
        { headers }
      )
      .then((res: any) => {
        const restored = res.data?.data?.restored_user_ids?.length ?? 0;
        notify(`Instrument unblocked — ${restored} user(s) restored`, "info", "", undefined, 6000);
        closeDialog();
        setActionProcessing(false);
        // Refresh instruments list + main profile (current user may have been restored).
        if (selectedUserId) {
          fetchBlockedInstruments(selectedUserId);
          fetchDetail(selectedUserId);
        }
      })
      .catch((err: any) => {
        notify(err?.response?.data?.message || "Failed to unblock instrument", "error", "", undefined, 8000);
        setActionProcessing(false);
      });
  };

  // ── quick toggle block from list ────────────────────────────────────────────

  const handleQuickToggle = (e: React.MouseEvent, user: any) => {
    e.stopPropagation();
    setSelectedUserId(user.user_id || user.id);
    openDialog(user.is_blocked ? "unblock" : "block");
  };

  // ── renders ──────────────────────────────────────────────────────────────────

  const profile = detailData?.profile;

  const renderDetailView = () => (
    <div style={{ width: "100%", boxSizing: "border-box", overflow: "hidden" }}>
      <Button startIcon={<ArrowBackIcon />} onClick={backToList} style={{ marginBottom: 20 }}>
        Back to List
      </Button>

      {detailLoading ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <CircularProgress />
        </div>
      ) : !profile ? (
        <Typography>No data found.</Typography>
      ) : (
        <>
          <Paper className={classes.detailCard}>
            <Typography variant="h6" className={classes.profileHeader}>
              User Profile — #{profile.user_id}
            </Typography>

            {/* Row 1: identity */}
            <div className={classes.detailRow}>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Email</span>
                <span className={classes.detailValue}>{profile.email || "N/A"}</span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Name</span>
                <span className={classes.detailValue}>
                  {`${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "N/A"}
                </span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Phone</span>
                <span className={classes.detailValue}>{profile.phone || "N/A"}</span>
              </div>
            </div>

            {/* Row 2: account */}
            <div className={classes.detailRow}>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Account Created</span>
                <span className={classes.detailValue}>{formatDate(profile.created_at)}</span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>AML Status</span>
                <span className={classes.detailValue}>{profile.aml_verification_status || "N/A"}</span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Trust Tier</span>
                <span className={classes.detailValue}>{profile.trust_tier ?? "N/A"}</span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Risk Score</span>
                <span className={classes.detailValue}>{profile.risk_score ?? "N/A"}</span>
              </div>
            </div>

            {/* Row 3: fraud status */}
            <div className={classes.detailRow}>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Blocked</span>
                <span className={classes.detailValue}>{profile.is_blocked ? "Yes" : "No"}</span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Flag</span>
                <span className={classes.detailValue}>
                  <FlagChip flag={profile.flag} watchRemaining={profile.watch_remaining} />
                </span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Flag Reason</span>
                <span className={classes.detailValue}>{profile.flag_reason || "—"}</span>
              </div>
            </div>

            {/* Row 4: payments */}
            <div className={classes.detailRow}>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Lifetime Spend</span>
                <span className={classes.detailValue}>
                  {profile.total_lifetime_spend != null ? `£${profile.total_lifetime_spend}` : "N/A"}
                </span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Payment Count</span>
                <span className={classes.detailValue}>{profile.payment_count ?? "N/A"}</span>
              </div>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Last Payment At</span>
                <span className={classes.detailValue}>{formatDate(profile.last_payment_at)}</span>
              </div>
            </div>

            {/* Row 5: meta */}
            <div className={classes.detailRow}>
              <div className={classes.detailField}>
                <span className={classes.detailLabel}>Profile Updated</span>
                <span className={classes.detailValue}>{formatDate(profile.updated_at)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className={classes.actionDivider}>
              {profile.is_blocked ? (
                <Button variant="contained" color="primary" onClick={() => openDialog("unblock")}>
                  Unblock
                </Button>
              ) : (
                <Button variant="contained" color="secondary" onClick={() => openDialog("block")}>
                  Block
                </Button>
              )}
              <Button
                variant="outlined"
                disabled={profile.flag === "suspicious"}
                onClick={() => openDialog("flag", "suspicious")}
              >
                Flag as Suspicious
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                disabled={profile.flag === "confirmed_fraud"}
                onClick={() => openDialog("flag", "confirmed_fraud")}
              >
                Flag as Confirmed Fraud
              </Button>
              <Button
                variant="contained"
                disabled={profile.flag === "bank_confirmed_fraud"}
                style={profile.flag === "bank_confirmed_fraud" ? undefined : { backgroundColor: "#8b0000", color: "#fff" }}
                onClick={() => openDialog("flag", "bank_confirmed_fraud")}
              >
                Flag as Bank Confirmed Fraud
              </Button>
              {(profile.is_blocked || (profile.flag && profile.flag !== "none")) && (
                <Button variant="outlined" onClick={() => openDialog("clear")}>
                  Clear
                </Button>
              )}
            </div>
          </Paper>

          {/* Signals table */}
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Recent Signals (last 50)
          </Typography>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Signal Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Blocked</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Acct Age</TableCell>
                  <TableCell>Onboarded</TableCell>
                  <TableCell>Metadata</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detailData?.signals || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className={classes.emptyState}>
                      No signals found.
                    </TableCell>
                  </TableRow>
                ) : (
                  (detailData.signals || []).map((s: any, i: number) => {
                    const meta = typeof s.metadata === "string" ? JSON.parse(s.metadata) : (s.metadata || {});
                    const extraKeys = Object.keys(meta).filter(
                      k => !["brand_name", "account_age_days", "is_onboarded", "reason", "triggered_rules", "is_auto_save", "payment_type", "watch_mode"].includes(k)
                    );
                    return (
                    <TableRow key={i}>
                      <TableCell>{s.signal_type}</TableCell>
                      <TableCell>{s.amount != null ? `£${s.amount}` : "N/A"}</TableCell>
                      <TableCell>{s.trust_tier ?? "N/A"}</TableCell>
                      <TableCell>{s.gate_mode || "N/A"}</TableCell>
                      <TableCell>{s.blocked ? "Yes" : "No"}</TableCell>
                      <TableCell>{meta.brand_name || "—"}</TableCell>
                      <TableCell>{meta.account_age_days != null ? `${meta.account_age_days}d` : "—"}</TableCell>
                      <TableCell>{meta.is_onboarded != null ? (meta.is_onboarded ? "Yes" : "No") : "—"}</TableCell>
                      <TableCell style={{ maxWidth: 200, fontSize: "0.75rem", color: "#666" }}>
                        {meta.payment_type && <div>type: {meta.payment_type}</div>}
                        {meta.watch_mode && <div>watch_mode: true</div>}
                        {meta.reason && meta.reason !== s.signal_type && <div>reason: {meta.reason}</div>}
                        {extraKeys.map(k => <div key={k}>{k}: {String(meta[k])}</div>)}
                      </TableCell>
                      <TableCell>{formatDate(s.created_at)}</TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Graph edges table */}
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Graph Edges
          </Typography>
          <TableContainer component={Paper} className={classes.tableContainer} style={{ marginBottom: 40 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Entity Type</TableCell>
                  <TableCell>Entity Value</TableCell>
                  <TableCell>Relationship</TableCell>
                  <TableCell>Signal Count</TableCell>
                  <TableCell>First Seen</TableCell>
                  <TableCell>Last Seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detailData?.graph_edges || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className={classes.emptyState}>
                      No graph edges found.
                    </TableCell>
                  </TableRow>
                ) : (
                  (detailData.graph_edges || []).map((e: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{e.entity_b_type}</TableCell>
                      <TableCell>{e.entity_b_value}</TableCell>
                      <TableCell>{e.relationship}</TableCell>
                      <TableCell>{e.signal_count}</TableCell>
                      <TableCell>{formatDate(e.first_seen_at)}</TableCell>
                      <TableCell>{formatDate(e.last_seen_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Blocked Instruments — lazy-loaded on expand */}
          <Typography
            variant="subtitle1"
            className={classes.sectionTitle}
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={toggleInstrumentsSection}
          >
            {instrumentsExpanded ? "▾" : "▸"} Blocked Instruments
            {instruments !== null && ` (${instruments.length})`}
          </Typography>
          {instrumentsExpanded && (
            <TableContainer component={Paper} className={classes.tableContainer} style={{ marginBottom: 40 }}>
              {instrumentsLoading ? (
                <div style={{ textAlign: "center", padding: 32 }}>
                  <CircularProgress />
                </div>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Fingerprint</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Blocked At</TableCell>
                      <TableCell>Blocked By</TableCell>
                      <TableCell>Unblocked At</TableCell>
                      <TableCell>Unblock Reason</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(instruments || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className={classes.emptyState}>
                          No blocked instruments linked to this user.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (instruments || []).map((inst: any) => {
                        const isActive = !inst.unblocked_at;
                        const fp = String(inst.fingerprint || "");
                        const reasonColor =
                          inst.reason === "user_confirmed_fraud"
                            ? "secondary"
                            : "default";
                        return (
                          <TableRow key={inst.id}>
                            <TableCell>
                              <Chip
                                label={isActive ? "Active" : "Unblocked"}
                                size="small"
                                className={isActive ? classes.blockedChip : undefined}
                                style={!isActive ? { backgroundColor: "#9e9e9e", color: "#fff" } : undefined}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={fp}>
                                <span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                                  {fp.slice(0, 12)}…
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={String(inst.reason || "").replace(/_/g, " ")}
                                size="small"
                                color={reasonColor as any}
                              />
                            </TableCell>
                            <TableCell>{formatDate(inst.created_at)}</TableCell>
                            <TableCell>{inst.blocked_by ?? "Auto"}</TableCell>
                            <TableCell>{formatDate(inst.unblocked_at)}</TableCell>
                            <TableCell style={{ maxWidth: 180 }}>
                              {inst.unblock_reason ? (
                                <Tooltip title={inst.unblock_reason}>
                                  <span style={{
                                    display: "inline-block",
                                    maxWidth: 180,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    verticalAlign: "bottom",
                                  }}>
                                    {inst.unblock_reason}
                                  </span>
                                </Tooltip>
                              ) : "—"}
                            </TableCell>
                            <TableCell>
                              {isActive ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="secondary"
                                  onClick={() => openInstrumentUnblockDialog(inst)}
                                >
                                  Unblock
                                </Button>
                              ) : (
                                <span style={{ color: "#9e9e9e", fontSize: "0.8rem" }}>Already unblocked</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          )}

          <Typography
            variant="subtitle1"
            className={classes.sectionTitle}
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={toggleDiditSection}
          >
            {diditExpanded ? "▾" : "▸"} Identity Verification (Didit)
            {didit !== null && ` (${(didit.verifications || []).length})`}
          </Typography>
          {diditExpanded && (
            <div style={{ marginBottom: 40 }}>
              {diditLoading ? (
                <div style={{ textAlign: "center", padding: 32 }}>
                  <CircularProgress />
                </div>
              ) : (didit?.verifications || []).length === 0 ? (
                <Typography variant="body2" className={classes.emptyState}>
                  No identity verification attempts for this user.
                </Typography>
              ) : (
                <>
                  <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Attempts</TableCell>
                          <TableCell>Document</TableCell>
                          <TableCell>Liveness</TableCell>
                          <TableCell>Face Match</TableCell>
                          <TableCell>Extracted Name</TableCell>
                          <TableCell>DOB</TableCell>
                          <TableCell>Doc Type</TableCell>
                          <TableCell>Updated</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(didit.verifications || []).map((v: any) => (
                          <TableRow key={v.id}>
                            <TableCell>{v.id}</TableCell>
                            <TableCell>
                              <Chip
                                label={v.source === "fraud" ? "fraud" : "mortgage kyc"}
                                size="small"
                                style={{
                                  backgroundColor: v.source === "fraud" ? "#7b1fa2" : "#546e7a",
                                  color: "#fff",
                                }}
                              />
                            </TableCell>
                            <TableCell>{v.status || "—"}</TableCell>
                            <TableCell>{v.attempt_count ?? 0}</TableCell>
                            <TableCell>{v.doc_verify_result || "—"}</TableCell>
                            <TableCell>{v.liveness_result || "—"}</TableCell>
                            <TableCell>{v.face_match_result || "—"}</TableCell>
                            <TableCell>
                              {[v.first_name, v.last_name].filter(Boolean).join(" ") || "—"}
                            </TableCell>
                            <TableCell>{v.dob || "—"}</TableCell>
                            <TableCell>{v.document_type || "—"}</TableCell>
                            <TableCell>{formatDate(v.status_updated_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                    API Logs
                  </Typography>
                  <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Created At</TableCell>
                          <TableCell>Verification</TableCell>
                          <TableCell>Endpoint</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Latency</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(didit.api_logs || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className={classes.emptyState}>
                              No API logs
                            </TableCell>
                          </TableRow>
                        ) : (
                          (didit.api_logs || []).map((l: any) => (
                            <TableRow key={l.id}>
                              <TableCell>{formatDate(l.createdAt)}</TableCell>
                              <TableCell>{l.didit_verification_id ?? "—"}</TableCell>
                              <TableCell>{l.endpoint}</TableCell>
                              <TableCell>{l.status_code ?? "—"}</TableCell>
                              <TableCell>{l.latency_ms != null ? `${l.latency_ms}ms` : "—"}</TableCell>
                              <TableCell>
                                {l.error_message ? (
                                  <Tooltip title={l.error_message}>
                                    <span style={{
                                      display: "inline-block",
                                      maxWidth: 240,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      verticalAlign: "bottom",
                                    }}>
                                      {l.error_message}
                                    </span>
                                  </Tooltip>
                                ) : "—"}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderListView = () => (
    <div style={{ width: "100%", boxSizing: "border-box" }}>
      <div className={classes.topBar}>
        <TextField
          label="Search by email"
          variant="outlined"
          size="small"
          value={filters.email}
          onChange={handleEmailChange}
          className={classes.emailInput}
          placeholder="prefix search..."
        />
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Status</InputLabel>
          <Select value={filters.status} onChange={handleStatusChange} label="Status">
            <MenuItem value="blocked">Blocked</MenuItem>
            <MenuItem value="flagged">Flagged</MenuItem>
            <MenuItem value="watching">Watching</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Flag</InputLabel>
          <Select value={filters.flag} onChange={handleFlagChange} label="Flag">
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="suspicious">Suspicious</MenuItem>
            <MenuItem value="confirmed_fraud">Confirmed Fraud</MenuItem>
            <MenuItem value="bank_confirmed_fraud">Bank Confirmed Fraud</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => openDialog("blockNew")}
        >
          Block New User
        </Button>
      </div>

      {listLoading ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Flag</TableCell>
                  <TableCell>Signal Count</TableCell>
                  <TableCell>Last Signal At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className={classes.emptyState}>
                      No results found.
                    </TableCell>
                  </TableRow>
                ) : (
                  listData.map((user: any, i: number) => (
                    <TableRow
                      key={user.user_id || user.id || i}
                      className={classes.clickableRow}
                      onClick={() => openDetail(user.user_id || user.id)}
                    >
                      <TableCell>{user.user_id || user.id}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {`${user.first_name || ""} ${user.last_name || ""}`.trim() || "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_blocked ? "Blocked" : "Active"}
                          size="small"
                          className={user.is_blocked ? classes.blockedChip : classes.activeChip}
                        />
                      </TableCell>
                      <TableCell>
                        <FlagChip flag={user.flag} watchRemaining={user.watch_remaining} className={classes.flagChip} />
                      </TableCell>
                      <TableCell>{user.signal_count ?? "—"}</TableCell>
                      <TableCell>{formatDate(user.last_signal_at)}</TableCell>
                      <TableCell>
                        <Tooltip title={user.is_blocked ? "Unblock user" : "Block user"}>
                          <IconButton
                            size="small"
                            color={user.is_blocked ? "primary" : "secondary"}
                            onClick={(e) => handleQuickToggle(e, user)}
                          >
                            {user.is_blocked ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <div className={classes.paginationBar}>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePrevPage}
              disabled={!pagination.has_previous_page || listLoading}
            >
              Previous
            </Button>
            <Typography variant="body2">
              Page {pagination.page} of {pagination.total_pages} — Total {pagination.total} records
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleNextPage}
              disabled={!pagination.has_next_page || listLoading}
            >
              Next
            </Button>
            <FormControl variant="outlined" size="small" className={classes.pageSizeControl}>
              <InputLabel>Size</InputLabel>
              <Select value={filters.pageSize} onChange={handlePageSizeChange} label="Size">
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </div>
        </>
      )}
    </div>
  );

  // ── dialog titles / content ──────────────────────────────────────────────────

  const dialogTitle = () => {
    if (dialogType === "block") return `Block User ${selectedUserId}`;
    if (dialogType === "unblock") return `Unblock User ${selectedUserId}`;
    if (dialogType === "flag") return `Flag User ${selectedUserId} as ${dialogFlag?.replace("_", " ")}`;
    if (dialogType === "clear") return `Clear User ${selectedUserId}`;
    if (dialogType === "blockNew") return "Block New User";
    if (dialogType === "instrumentUnblock") return `Unblock Instrument #${instrumentToUnblock?.id}`;
    return "";
  };

  const confirmLabel = () => {
    if (dialogType === "block" || dialogType === "blockNew") return "Block";
    if (dialogType === "unblock" || dialogType === "instrumentUnblock") return "Unblock";
    if (dialogType === "flag") return "Confirm";
    if (dialogType === "clear") return "Clear";
    return "Confirm";
  };

  const isConfirmDisabled = () => {
    if (actionProcessing) return true;
    if (dialogType === "blockNew" && !dialogUserId.trim()) return true;
    if (dialogType === "instrumentUnblock" && instrumentUnblockReason.trim().length < 10) return true;
    return false;
  };

  return (
    <div className={classes.main}>
      {view === "list" ? renderListView() : renderDetailView()}

      {/* ── Dialog ─────────────────────────────────────────────────────────── */}
      <Dialog open={dialogType !== null} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogTitle()}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {dialogType === "block" && (
              <>
                <p>This will block the user from making payments.</p>
                <TextField
                  label="Reason (optional)"
                  value={dialogReason}
                  onChange={(e) => setDialogReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  style={{ marginTop: 8 }}
                />
              </>
            )}
            {dialogType === "unblock" && (
              <p>Are you sure you want to unblock this user?</p>
            )}
            {dialogType === "flag" && (
              <>
                <p>
                  Set flag to <strong>{dialogFlag?.replace("_", " ")}</strong> for this user.
                </p>
                <TextField
                  label="Reason (optional)"
                  value={dialogReason}
                  onChange={(e) => setDialogReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  style={{ marginTop: 8 }}
                />
              </>
            )}
            {dialogType === "clear" && (
              <p>This will unblock the user and reset their flag to none.</p>
            )}
            {dialogType === "blockNew" && (
              <>
                <p>Block a user who does not yet have a fraud risk profile. A profile will be created.</p>
                <TextField
                  label="User ID"
                  value={dialogUserId}
                  onChange={(e) => setDialogUserId(e.target.value)}
                  fullWidth
                  variant="outlined"
                  style={{ marginTop: 8 }}
                  required
                />
                <TextField
                  label="Reason (optional)"
                  value={dialogReason}
                  onChange={(e) => setDialogReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  style={{ marginTop: 8 }}
                />
              </>
            )}
            {dialogType === "instrumentUnblock" && instrumentToUnblock && (
              <>
                <p>
                  This will unblock the bank account fingerprint{" "}
                  <code style={{ background: "#f5f5f5", padding: "2px 4px" }}>
                    {String(instrumentToUnblock.fingerprint || "").slice(0, 12)}…
                  </code>
                  . Future payments from this account will be allowed.
                </p>
                <p style={{ color: "#e65100", fontSize: "0.85rem" }}>
                  <span role="img" aria-label="warning">⚠️</span>{" "}
                  If this instrument was blocked because a user was confirmed fraud, unblocking
                  it will also restore those users (they will be placed on watch mode for next 5 transactions).
                  Shared-account blocks involving multiple users are not auto-restored.
                </p>
                <TextField
                  label="Unblock reason (required, min 10 characters)"
                  value={instrumentUnblockReason}
                  onChange={(e) => setInstrumentUnblockReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  style={{ marginTop: 8 }}
                  required
                  placeholder="e.g. verified legit couple, joint mortgage not yet in credit report"
                />
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="primary" disabled={actionProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleDialogConfirm}
            color="secondary"
            variant="contained"
            disabled={isConfirmDisabled()}
          >
            {actionProcessing ? <CircularProgress size={20} /> : confirmLabel()}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default FraudReview;
